import { useState } from 'react';
import { useAdminAuth } from '../../../hooks/useAdminAuth';

interface LoginModalProps {
  onClose: () => void;
  onSwitchToSignup?: () => void;
  onAdminLogin?: () => void;
}

type Step = 'login' | 'forgot' | 'forgot-sent' | 'admin-success';

export default function LoginModal({ onClose, onSwitchToSignup, onAdminLogin }: LoginModalProps) {
  const { login } = useAdminAuth();
  const [step, setStep] = useState<Step>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = 'Please enter a valid email address';
    }
    if (password.length < 6) {
      errs.password = 'Password must be at least 6 characters';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 1100));
    const result = login(email, password);
    setIsLoading(false);
    if (result.isAdmin) {
      setStep('admin-success');
      setTimeout(() => {
        onAdminLogin?.();
        onClose();
      }, 2200);
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col"
        style={{ animation: 'loginSlideIn 0.28s cubic-bezier(0.34,1.56,0.64,1)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── ADMIN SUCCESS STEP ── */}
        {step === 'admin-success' && (
          <>
            <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-400 px-8 py-7 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 flex items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm flex-shrink-0">
                    <i className="ri-vip-crown-2-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-white/75 text-xs font-semibold uppercase tracking-widest">Admin Access Granted</p>
                    <h2 className="text-white text-xl font-extrabold leading-tight">Welcome, Jillersaint!</h2>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-8 py-8 flex flex-col items-center gap-5">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-yellow-400 shadow-xl">
                <i className="ri-shield-star-fill text-white text-3xl"></i>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-extrabold text-gray-900 mb-2">100% Admin Access Unlocked</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  You&apos;re signed in as <strong>Jillersaint@softypanda.com</strong> with full
                  <span className="text-amber-600 font-bold"> Golden Pro</span> access.<br />
                  All tools, limits bypassed, admin rights active.
                </p>
              </div>
              <div className="w-full flex flex-col gap-2">
                {[
                  { icon: 'ri-infinity-line', label: 'Unlimited generations — no limits' },
                  { icon: 'ri-tools-line', label: 'All 11 AI tools unlocked' },
                  { icon: 'ri-shield-user-line', label: 'Full admin rights enabled' },
                  { icon: 'ri-vip-crown-line', label: 'Golden Pro plan — all features' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-400 flex-shrink-0">
                      <i className={`${item.icon} text-white text-sm`}></i>
                    </div>
                    <span className="text-sm font-semibold text-gray-800">{item.label}</span>
                    <i className="ri-check-line text-amber-500 text-sm ml-auto flex-shrink-0"></i>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400 animate-pulse">
                <i className="ri-loader-4-line animate-spin text-amber-400"></i>
                Redirecting to dashboard…
              </div>
            </div>
          </>
        )}

        {/* ── HEADER (non-admin steps) ── */}
        {step !== 'admin-success' && (
          <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-cyan-500 px-8 py-7 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 flex items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm flex-shrink-0">
                  <i className="ri-user-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-white/75 text-xs font-semibold uppercase tracking-widest">
                    {step === 'login' ? 'Welcome back' : step === 'forgot' ? 'Reset password' : 'Check your inbox'}
                  </p>
                  <h2 className="text-white text-xl font-extrabold leading-tight">
                    {step === 'login' ? 'Sign in to 1WORLD-AI' : step === 'forgot' ? 'Forgot Password' : 'Email Sent!'}
                  </h2>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-white text-lg"></i>
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="px-8 py-7">

          {/* ── LOGIN STEP ── */}
          {step === 'login' && (
            <>
              {/* Social buttons */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-700 transition-all cursor-pointer"
                >
                  <i className="ri-google-fill text-base" style={{ color: '#4285F4' }}></i>
                  Google
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-700 transition-all cursor-pointer"
                >
                  <i className="ri-github-fill text-base text-gray-900"></i>
                  GitHub
                </button>
              </div>

              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs font-semibold text-gray-400">or sign in with email</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                    <input
                      type="email"
                      value={email}
                      onChange={e => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: '' })); }}
                      placeholder="jane@example.com"
                      className={`w-full pl-11 pr-4 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                        errors.email
                          ? 'border-red-300 focus:ring-red-200'
                          : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                      }`}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line text-xs"></i>{errors.email}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest">Password</label>
                    <button
                      type="button"
                      onClick={() => setStep('forgot')}
                      className="text-xs font-semibold text-violet-600 hover:text-violet-800 transition-colors cursor-pointer"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: '' })); }}
                      placeholder="Your password"
                      className={`w-full pl-11 pr-12 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                        errors.password
                          ? 'border-red-300 focus:ring-red-200'
                          : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line text-xs"></i>{errors.password}
                    </p>
                  )}
                </div>

                {/* Remember me */}
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                      rememberMe ? 'bg-violet-600 border-violet-600' : 'border-gray-300 group-hover:border-violet-400'
                    }`}>
                      {rememberMe && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                  </div>
                  <span className="text-sm text-gray-600">Remember me for 30 days</span>
                </label>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-bold rounded-full hover:shadow-xl hover:scale-[1.02] transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed mt-2"
                >
                  {isLoading ? (
                    <><i className="ri-loader-4-line animate-spin text-base"></i>Signing in…</>
                  ) : (
                    <><i className="ri-login-box-line text-base"></i>Sign In</>
                  )}
                </button>
              </form>

              {/* Sign up link */}
              <p className="text-center text-sm text-gray-500 mt-5">
                Don&apos;t have an account?{' '}
                <button
                  onClick={onSwitchToSignup}
                  className="text-violet-600 font-bold hover:underline cursor-pointer"
                >
                  Get started free
                </button>
              </p>
            </>
          )}

          {/* ── FORGOT PASSWORD STEP ── */}
          {step === 'forgot' && (
            <>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                Enter your email and we&apos;ll send you a link to reset your password.
              </p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!forgotEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
                  setForgotError('Please enter a valid email address');
                  return;
                }
                setForgotError('');
                setIsLoading(true);
                await new Promise(r => setTimeout(r, 1000));
                setIsLoading(false);
                setStep('forgot-sent');
              }} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={e => { setForgotEmail(e.target.value); setForgotError(''); }}
                      placeholder="jane@example.com"
                      className={`w-full pl-11 pr-4 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                        forgotError
                          ? 'border-red-300 focus:ring-red-200'
                          : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                      }`}
                    />
                  </div>
                  {forgotError && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line text-xs"></i>{forgotError}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-bold rounded-full hover:shadow-xl hover:scale-[1.02] transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <><i className="ri-loader-4-line animate-spin text-base"></i>Sending…</>
                  ) : (
                    <><i className="ri-send-plane-line text-base"></i>Send Reset Link</>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setStep('login')}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-base"></i>
                  Back to Sign In
                </button>
              </form>
            </>
          )}

          {/* ── FORGOT SENT STEP ── */}
          {step === 'forgot-sent' && (
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 shadow-xl mb-5">
                <i className="ri-mail-check-line text-white text-3xl"></i>
              </div>
              <h3 className="text-xl font-extrabold text-gray-900 mb-2">Check your inbox</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-2">
                We&apos;ve sent a password reset link to
              </p>
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full mb-6">
                <i className="ri-mail-line text-emerald-500 text-sm"></i>
                <span className="text-emerald-700 text-sm font-bold">{forgotEmail}</span>
              </div>
              <p className="text-xs text-gray-400 mb-6">
                Didn&apos;t receive it? Check your spam folder or{' '}
                <button
                  onClick={() => { setStep('forgot'); setForgotEmail(''); }}
                  className="text-violet-600 font-semibold hover:underline cursor-pointer"
                >
                  try again
                </button>
              </p>
              <button
                onClick={() => setStep('login')}
                className="w-full py-3.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-bold rounded-full hover:shadow-xl hover:scale-[1.02] transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-login-box-line text-base"></i>
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes loginSlideIn {
          from { opacity: 0; transform: scale(0.92) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
